import { Pencil1Icon } from '@radix-ui/react-icons'
import {
  Code,
  DataList,
  Flex,
  IconButton,
  Link,
  Table,
  Text,
  VisuallyHidden,
} from '@radix-ui/themes'
import { isNil } from 'lodash'
import {
  RewardTypeSummary,
  RewardPostWinConfigText,
  RewardScheduleWithGame,
} from '@Generated/back-office-client/src'
import { formatToCurrencyDisplay } from '@Components/util/utils'
import { EndEarlyButton } from './end-early-button'
import { sortPrizes } from './types'

const formatQuantity = (reward: RewardTypeSummary): string => {
  if (!isNil(reward.quantity)) {
    return `${reward.quantity}`
  }
  if (!isNil(reward.quantityPercentage)) {
    return `${reward.quantityPercentage}%`
  }
  return '-'
}

// Labels are medium-weight gray so they read as labels, not as part of the value
const Label = ({ children }: { children: string }) => (
  <DataList.Label minWidth="80px">
    <Text size="1" weight="medium" color="gray">
      {children}
    </Text>
  </DataList.Label>
)

const PostWinConfigDetails = ({ config }: { config: RewardPostWinConfigText }) => {
  const metadataEntries = Object.entries(config.ctaMetadata ?? {})

  return (
    <Flex direction="column" gap="2">
      <Flex direction="column" gap="1">
        <Text size="2" weight="bold">
          {config.header}
        </Text>
        <Text size="2">{config.description}</Text>
        {config.disclaimer ? (
          <Text size="1" color="gray">
            {config.disclaimer}
          </Text>
        ) : null}
      </Flex>
      <DataList.Root size="1" orientation="horizontal">
        <DataList.Item>
          <Label>Button</Label>
          <DataList.Value>
            {config.callToAction ? (
              <Text size="2">{config.callToAction}</Text>
            ) : (
              <Text size="2" color="gray">
                No button
              </Text>
            )}
          </DataList.Value>
        </DataList.Item>
        <DataList.Item>
          <Label>Metadata</Label>
          <DataList.Value>
            {metadataEntries.length === 0 ? (
              <Text size="2" color="gray">
                No metadata
              </Text>
            ) : (
              <Flex direction="column" gap="1">
                {metadataEntries.map(([key, value]) => (
                  <Flex key={key} gap="2" align="center">
                    <Code variant="ghost">{key}</Code>
                    <Code>{value}</Code>
                  </Flex>
                ))}
              </Flex>
            )}
          </DataList.Value>
        </DataList.Item>
      </DataList.Root>
    </Flex>
  )
}

const PrizeTable = ({
  rewards,
  onEditPrize,
}: {
  rewards: RewardTypeSummary[]
  onEditPrize?: (reward: RewardTypeSummary) => void
}) => {
  if (rewards.length === 0) {
    return (
      <Text size="2" color="gray">
        No prizes in this schedule.
      </Text>
    )
  }

  return (
    <Table.Root size="1">
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Prize</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Category</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Rarity</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Quantity</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Cash value</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Wheels</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Post-win</Table.ColumnHeaderCell>
          {onEditPrize ? (
            <Table.ColumnHeaderCell>
              <VisuallyHidden>Actions</VisuallyHidden>
            </Table.ColumnHeaderCell>
          ) : null}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {sortPrizes(rewards).map((reward) => (
          <Table.Row key={reward.id}>
            <Table.RowHeaderCell>{reward.rewardName}</Table.RowHeaderCell>
            <Table.Cell>{reward.rewardCategory}</Table.Cell>
            <Table.Cell>{reward.rarity}</Table.Cell>
            <Table.Cell>{formatQuantity(reward)}</Table.Cell>
            <Table.Cell>{formatToCurrencyDisplay(reward.cashValueInCents)}</Table.Cell>
            <Table.Cell>{reward.wheelIds.join(', ')}</Table.Cell>
            <Table.Cell>
              <PostWinConfigDetails config={reward.postWinConfig} />
            </Table.Cell>
            {onEditPrize ? (
              <Table.Cell>
                <IconButton
                  size="1"
                  variant="ghost"
                  aria-label={`Edit prize ${reward.rewardName}`}
                  onClick={() => onEditPrize(reward)}>
                  <Pencil1Icon />
                </IconButton>
              </Table.Cell>
            ) : null}
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
}

// The body of a schedule's accordion row; the name, dates, and status badge live in the row header
export const PrizeScheduleDetails = ({
  schedule,
  onEditPrize,
  canEndEarly = false,
}: {
  schedule: RewardScheduleWithGame
  // Omitted for users who can't edit
  onEditPrize?: (reward: RewardTypeSummary) => void
  canEndEarly?: boolean
}) => {
  return (
    <Flex direction="column" gap="3" className="AccordionContentText">
      <Flex justify="between" align="start" gap="3">
        <DataList.Root size="2" orientation="horizontal">
          <DataList.Item>
            <Label>Promo text</Label>
            <DataList.Value>
              {schedule.promoText.length > 0 ? (
                <Text size="2">{schedule.promoText.join(' · ')}</Text>
              ) : (
                <Text size="2" color="gray">
                  None
                </Text>
              )}
            </DataList.Value>
          </DataList.Item>
          <DataList.Item>
            <Label>Links</Label>
            <DataList.Value>
              {schedule.supportLink || schedule.rulesLink ? (
                <Flex gap="3">
                  {schedule.supportLink ? (
                    <Link size="2" href={schedule.supportLink} target="_blank" rel="noreferrer">
                      Support
                    </Link>
                  ) : null}
                  {schedule.rulesLink ? (
                    <Link size="2" href={schedule.rulesLink} target="_blank" rel="noreferrer">
                      Rules
                    </Link>
                  ) : null}
                </Flex>
              ) : (
                <Text size="2" color="gray">
                  None
                </Text>
              )}
            </DataList.Value>
          </DataList.Item>
        </DataList.Root>
        {canEndEarly ? <EndEarlyButton schedule={schedule} /> : null}
      </Flex>
      <PrizeTable rewards={schedule.rewards} onEditPrize={onEditPrize} />
    </Flex>
  )
}
